import {
  applicantSection,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { every, propertySection } from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F8. Wniosek o wydanie zaświadczenia o samodzielności lokalu — art. 2 ust. 1a–3 ustawy z 24.06.1994 r. o własności
 * lokali, art. 217 KPA; opłata 17 zł za zaświadczenie (część II załącznika do ustawy o opłacie skarbowej) — za każdy lokal.
 * Brak wzoru ogólnopolskiego; układ jak wniosek Starostwa w Kłodzku
 * (https://bip.powiat.klodzko.pl/download/attachment/17642/wniosek-o-wydanie-zaswiadczenia-o-samodzielnosci-lokalu.pdf —
 * pobrany 2026-10-22): wnioskodawca, pełnomocnik, rodzaj i liczba lokali, dane ewidencyjne nieruchomości, cel wydania,
 * pomieszczenia przynależne; numer uprawnień autora rzutu i opcjonalne dane pozwoleń — według „Uwag dla EBI” katalogu.
 * Zwolnienie lokali mieszkalnych (art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej) urzędy stosują różnie — do potwierdzenia
 * z wydziałem; poza rzutem wszystkie załączniki są praktyką urzędów (nieobowiązkowe).
 */
const EXEMPT: EbiCondition = every({ field: "rodzajLokali", equals: "mieszkalne" }, { field: "zwolnienie", equals: true });

const F8: IEbiFormDefinition = {
  code: "F8",
  version: 1,
  title: "Wniosek o wydanie zaświadczenia o samodzielności lokalu",
  category: EEbiFormCategory.Construction,
  office: EEbiFormOffice.County,
  description: "Zaświadczenie, że lokal mieszkalny albo użytkowy jest samodzielny — potrzebne do ustanowienia odrębnej własności lokalu.",
  keywords: ["samodzielność lokalu", "zaświadczenie", "lokal", "odrębna własność", "wyodrębnienie", "mieszkanie", "garaż"],
  organ: "Starosta (prezydent miasta na prawach powiatu) — wydział architektury i budownictwa",
  legalBasis: "art. 2 ust. 1a–3 ustawy z dnia 24 czerwca 1994 r. o własności lokali; art. 217 ustawy z dnia 14 czerwca 1960 r. — Kodeks postępowania administracyjnego",
  request:
    "Na podstawie art. 2 ust. 3 ustawy z dnia 24 czerwca 1994 r. o własności lokali wnoszę o wydanie zaświadczenia o samodzielności lokalu (lokali) opisanego we wniosku.",
  feeLabel: "17 zł",
  fee: "17 zł za każdy lokal (opłata skarbowa za zaświadczenie); zwolnienie lokali mieszkalnych stosowane przez urzędy różnie; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do 7 dni",
  deadline: "Bez zbędnej zwłoki, nie później niż w terminie 7 dni (art. 217 § 3 KPA).",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "lokale",
      title: "Lokale",
      fields: [
        {
          code: "rodzajLokali",
          type: "radio",
          label: "Rodzaj lokalu (lokali)",
          required: true,
          options: [
            { value: "mieszkalne", label: "mieszkalny (mieszkalne)" },
            { value: "inne", label: "o innym przeznaczeniu (np. garaż, lokal usługowy)" },
            { value: "oba", label: "mieszkalne i o innym przeznaczeniu" },
          ],
        },
        { code: "liczbaLokali", type: "number", label: "Liczba lokali", required: true, width: "third", min: 1, max: 999 },
        {
          code: "rokZakonczenia",
          type: "number",
          label: "Rok zakończenia budowy budynku (jeśli znany)",
          width: "third",
          min: 1000,
          max: 2100,
        },
        {
          code: "opisLokali",
          type: "textarea",
          label: "Opis lokali",
          required: true,
          maxLength: 2000,
          hint: "Np. „lokal mieszkalny nr 2 w budynku wielorodzinnym”, „dwa garaże w budynku usługowym” — z numerami lokali, jeśli zostały nadane.",
        },
        {
          code: "pomieszczeniaPrzynalezne",
          type: "radio",
          label: "Lokal (lokale)",
          required: true,
          options: [
            { value: "z", label: "wraz z pomieszczeniami przynależnymi (np. piwnica, strych, komórka, garaż)" },
            { value: "bez", label: "bez pomieszczeń przynależnych" },
          ],
        },
      ],
    },
    propertySection("Dane nieruchomości, na której znajduje się budynek"),
    {
      code: "dokumentyBudynku",
      title: "Dokumenty budynku (nieobowiązkowo)",
      fields: [
        { code: "pozwolenieNaBudowe", type: "text", label: "Numer i data pozwolenia na budowę (albo zgłoszenia)", maxLength: 200 },
        {
          code: "pozwolenieNaUzytkowanie",
          type: "text",
          label: "Numer i data pozwolenia na użytkowanie (albo zawiadomienia o zakończeniu budowy)",
          maxLength: 200,
        },
      ],
    },
    {
      code: "cel",
      title: "Cel wydania zaświadczenia",
      fields: [
        {
          code: "celWydania",
          type: "textarea",
          label: "Cel wydania zaświadczenia (interes prawny)",
          required: true,
          maxLength: 1000,
          placeholder: "np. ustanowienie odrębnej własności lokalu i jego sprzedaż",
        },
      ],
    },
    {
      code: "autorRzutu",
      title: "Autor rzutu kondygnacji albo inwentaryzacji",
      fields: [
        { code: "autorNazwa", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
        {
          code: "autorUprawnienia",
          type: "text",
          label: "Numer uprawnień budowlanych (albo uprawnień rzeczoznawcy)",
          required: true,
          width: "half",
          maxLength: 100,
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      fields: [
        {
          code: "zwolnienie",
          type: "checkbox",
          label:
            "Wnoszę o zwolnienie z opłaty skarbowej — zaświadczenie dotyczy wyłącznie lokali mieszkalnych (sprawy budownictwa mieszkaniowego, art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej)",
          visibleWhen: { field: "rodzajLokali", equals: "mieszkalne" },
          hint: "Urzędy stosują to zwolnienie różnie — urząd potwierdzi, czy opłata jest należna.",
        },
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa do wniesienia",
          feeAmount: 17,
          feeUnitField: "liczbaLokali",
          feeWaivedWhen: EXEMPT,
          hint: "17 zł za każdy lokal — dołącz potwierdzenie przelewu na rachunek urzędu.",
        },
      ],
    },
    replySection("Sposób odbioru zaświadczenia"),
  ],
  attachments: [
    {
      code: "rzutLokalu",
      label: "Rzut odpowiedniej kondygnacji budynku z zaznaczeniem lokalu i pomieszczeń przynależnych albo operat inwentaryzacyjny lokalu",
      hint: "Sporządzony i podpisany przez osobę z uprawnieniami budowlanymi albo rzeczoznawcę (podpis elektroniczny albo skan dokumentu z podpisem).",
      multiple: true,
      required: true,
    },
    {
      code: "opisTechniczny",
      label: "Opis techniczny wydzielanych lokali i pomieszczeń przynależnych",
      hint: "(praktyka urzędów) — zestawienie powierzchni, przegrody, media; nieobowiązkowo.",
    },
    {
      code: "mapa",
      label: "Mapa z oznaczeniem położenia budynku (wyrys z operatu ewidencyjnego)",
      hint: "(praktyka urzędów) — nieobowiązkowo.",
    },
    {
      code: "dokumentLegalnosci",
      label: "Dokument potwierdzający legalność budynku (pozwolenie na budowę, pozwolenie na użytkowanie, zawiadomienie o zakończeniu budowy)",
      hint: "(praktyka urzędów) — gdy dokumentacji budynku nie ma w urzędzie.",
    },
    {
      code: "wypisPlanu",
      label: "Wypis i wyrys z planu miejscowego albo decyzja o warunkach zabudowy",
      hint: "(praktyka urzędów) — nieobowiązkowo.",
    },
    {
      code: "tytulPrawny",
      label: "Dokument potwierdzający tytuł prawny do lokalu lub nieruchomości (np. odpis z księgi wieczystej)",
      hint: "(praktyka urzędów) — nieobowiązkowo.",
    },
    ...proxyAttachments(),
    feeProofAttachment("Dowód zapłaty opłaty skarbowej (17 zł za każdy lokal)", { visibleWhen: { not: EXEMPT } }),
    otherAttachments(),
  ],
};

export default F8;
